import moment, { Moment } from "moment-timezone";
import { PERIOD_TYPE } from "./constants";

type DateLike = Moment | Date | string;

export const PDT_OFFSET = moment.tz("America/Los_Angeles").utcOffset();

export function getQuarterForMonth(month: number, year: number | null = null) {
    // Break the date down into quarters
    const quarter = Math.floor(month / 3);
    // And get the month of the quarter we want (Jan, Apr, Jul, Oct)
    const qMonth = quarter * 3 + 1;

    const qYear = year || new Date().getFullYear();
    return new Date(Date.UTC(qYear, qMonth - 1, 1, 0, 0, 0, 0));
}

/**
 * Returns the start of the quarter for a specific date
 */
export const getQuarterForDate = (date = new Date()) =>
    getQuarterForMonth(date.getMonth(), date.getFullYear());

export const formatYMD = (date: Date, useTime = false) => {
    if (!date) {
        return "";
    }

    const time = useTime ? " HH:mm" : "";
    return moment(date).format(`YYYY-MM-DD${time}`);
};

export const formatMonDD = (timestamp: number | Date | undefined) => {
    if (!timestamp) {
        return null;
    }
    const mts = moment.utc(timestamp);
    if (mts.year() !== moment().year()) {
        return mts.format("MMM DD 'YY");
    }
    return mts.format("MMM DD");
};

/**
 * Variation of moment's fromNow
 */
export function getTimeAgo(timestamp: string | null | undefined) {
    const answerTime = moment.utc(timestamp);
    const today = moment.utc().startOf("day");
    const tomorrow = moment.utc().subtract(1, "day").startOf("day");
    const thisWeek = moment.utc().startOf("week");
    const lastWeek = moment.utc().startOf("week").subtract(1, "weeks");
    const thisMonth = moment.utc().startOf("month");

    if (today.isBefore(answerTime)) {
        return "Today";
    }

    if (tomorrow.isBefore(answerTime)) {
        return "Yesterday";
    }

    if (thisWeek.isBefore(answerTime)) {
        return "This Week";
    }

    if (lastWeek.isBefore(answerTime)) {
        return "Last Week";
    }

    if (thisMonth.isBefore(answerTime)) {
        return "This Month";
    }

    return moment.utc(answerTime).format("MMM YYYY");
}

export const formatPeriodByType = (momentPeriod: Moment, periodType: string) =>
    periodType === PERIOD_TYPE.MONTHLY
        ? momentPeriod.format("YYYY-MM")
        : `Q${momentPeriod.quarter()} ${momentPeriod.format("YYYY")}`;

export const formatMonthName = (momentTime: Moment) => {
    let name = momentTime.format("MMM");
    if (momentTime.year() !== moment().year()) {
        name += ` ${momentTime.format("YY")}`;
    }
    return name;
};

export type TimeStamp = {
    month?: string;
    quarter?: string;
};

export const getPeriodFromTimestamp = (
    periodType: (typeof PERIOD_TYPE)[keyof typeof PERIOD_TYPE],
    timestamp: TimeStamp,
) => {
    if (periodType === PERIOD_TYPE.MONTHLY && timestamp.month) {
        const [year, month] = timestamp.month.split("-").map((n) => parseInt(n, 10)) as [
            number,
            number,
        ];
        return new Date(Date.UTC(year, month - 1, 1));
    }
    if (periodType === PERIOD_TYPE.QUARTERLY && timestamp.quarter) {
        const [quarter, year] = timestamp.quarter
            .substring(1)
            .split(" ")
            .map((n) => parseInt(n, 10)) as [number, number];
        const month = (quarter - 1) * 3 + 1;
        return new Date(Date.UTC(year, month - 1, 1));
    }

    return null;
};

/**
 * Attempts to take a duration and up convert it to use the most efficient method of writing it
 * into a human readable format.
 *
 * i.e. 279min = 4hr19min
 *
 * If 'abbreviateTop' is true, it'll show the 'highest' measurement with a greater than sign
 * to indicate that it's 'more than' a set amount of time ~*~if~*~ that measurement is a larger
 * one than the requested unit
 *
 * i.e. 3hr24min would read as >3hr if abbreviateTop is true
 *
 * If 'trimDays' is true, it'll round days down to the last day.  Used for smaller card displays
 * and other spots where knowing that something over a day is already long.
 *
 * Supports going up from ms to days (months require being aware of the date itself
 * and not just duration due to different month lengths)
 *
 * Durations are humanized to be 1 at their smallest unit as the implication of having
 * any duration at all means it's non-zero.
 *
 * @param {int} duration
 * @param {string} unit : 'ms', 'seconds', 'minutes', 'hours', 'days'
 * @param {abbreviateTop} bool
 */

export function formatDurationShortForm(
    duration: number,
    unit: string,
    abbreviateTop = false,
    trimDays = true,
) {
    const timeInUnits = new Map<string, number>();
    let currentUnit = unit;
    let currentDuration = duration;
    const unitAbbrevs = [
        ["ms", "ms"],
        ["seconds", "s"],
        ["minutes", "m"],
        ["hours", "hr"],
        ["days", "d"],
    ];

    if (currentUnit === "ms") {
        if (duration === 0 && timeInUnits.size === 0) {
            return `1ms`;
        }
        const nextSeconds = Math.floor(currentDuration / 1000);
        const remainder = currentDuration % 1000;
        if (remainder !== 0) {
            timeInUnits.set("ms", remainder);
        }
        currentDuration = nextSeconds;
        currentUnit = "seconds";
    }
    if (currentUnit === "seconds") {
        if (duration === 0 && timeInUnits.size === 0) {
            return `1s`;
        }
        const nextMinutes = Math.floor(currentDuration / 60);
        const remainder = currentDuration % 60;
        if (remainder !== 0) {
            timeInUnits.set("seconds", remainder);
        }
        currentDuration = nextMinutes;
        currentUnit = "minutes";
    }
    if (currentUnit === "minutes") {
        if (duration === 0 && timeInUnits.size === 0) {
            return `1min`;
        }
        const nextHours = Math.floor(currentDuration / 60);
        const remainder = currentDuration % 60;
        if (remainder !== 0) {
            timeInUnits.set("minutes", remainder);
        }
        currentDuration = nextHours;
        currentUnit = "hours";
    }
    if (currentUnit === "hours") {
        if (duration === 0 && timeInUnits.size === 0) {
            return `1hr`;
        }
        const nextDays = Math.floor(currentDuration / 24);
        const remainder = currentDuration % 24;
        if (remainder !== 0) {
            timeInUnits.set("hours", remainder);
        }
        currentDuration = nextDays;
        currentUnit = "days";
    }
    if (currentUnit === "days") {
        if (duration === 0 && timeInUnits.size === 0) {
            return `1d`;
        }
        if (unit === "days") {
            timeInUnits.set("days", currentDuration);
        } else if (currentDuration !== 0) {
            timeInUnits.set("days", currentDuration);
        }
    }
    const timeStrArray = [""];
    if (trimDays && timeInUnits.get("days")) {
        return `${timeInUnits.get("days") || 0}d`;
    }
    for (let i = unitAbbrevs.length - 1; i >= 0; i -= 1) {
        const measure = unitAbbrevs[i][0];
        if (timeInUnits.get(measure)) {
            if (abbreviateTop && timeInUnits.size > 1) {
                return `>${timeInUnits.get(measure) || 0}${unitAbbrevs[i][1]}`;
            }
            if (timeInUnits.size === 1) {
                return `${timeInUnits.get(measure) || 0}${unitAbbrevs[i][1]}`;
            }
            timeStrArray.push(`${timeInUnits.get(measure) || 0}${unitAbbrevs[i][1]}`);
        }
    }
    return timeStrArray.join("");
}

const commentsLocale = {
    relativeTime: {
        future: "in %s",
        past: "%s ago",
        s: "Today",
        ss: "Today",
        m: "Today",
        mm: "Today",
        h: "Today",
        hh: "Today",
        d: "Yesterday",
        dd: "%dd ago",
        w: "1w ago",
        ww: "%dw ago",
        M: "1mo ago",
        MM: "%dmo ago",
        y: "1y ago",
        yy: "%dy ago",
    },
};

export const defaultLocale = {
    relativeTime: {
        future: "in %s",
        past: "%s ago",
        s: "a few seconds",
        ss: "%d seconds",
        m: "1m",
        mm: "%dm",
        h: "1h",
        hh: "%dh",
        d: "1d",
        dd: "%dd",
        w: "a week",
        ww: "%d weeks",
        M: "a month",
        MM: "%dmo",
        y: "a year",
        yy: "%d years",
    },
};

export const setupMomentDefaults = () => {
    // Default locales, one custom for comments
    moment.updateLocale("en-comments", commentsLocale);
    moment.updateLocale("en", defaultLocale);
};

const localeWrap = (locale: string) => (date: DateLike) =>
    (moment.isMoment(date) ? date : moment(date)).locale(locale);

export const defaultMomentLocale = (date: DateLike) => localeWrap("en")(date);
export const commentMomentLocale = (date: DateLike) => localeWrap("en-comments")(date);

export const fromNow = (date: DateLike, withoutSuffix = false) =>
    defaultMomentLocale(date).fromNow(withoutSuffix);
